import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import TeamsContext from '@providers/Teams/TeamsContext';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { SectionTemplate } from '@/typings/questionnaires-api-models';
import DynamicForm from '@/components/Questionnaire/form/DynamicForm';
import { Questionnaire } from '@typings/Questionnaire';

type Props = { slug: string; usesCasebuilderQuestionnaire: boolean; };

const QuestionnairePreview: React.FC<Props> = ({ slug, usesCasebuilderQuestionnaire }) => {
  const { currentTeamOwner } = useContext(TeamsContext);
  const {
    data: questionnaireTemplate,
    getData: getQuestionnaireTemplate,
  } = useNoodleApi(tsClient.questionnaires.getQuestionnaireTemplateBySlug);

  const { getData: getFieldTemplates } = useNoodleApi(tsClient.questionnaires.getFieldTemplatesForSectionTemplate);

  const [sectionFields, setSectionFields] = useState<Record<string, unknown>>({});

  const creatorId = currentTeamOwner?.id;

  useEffect(() => {
    if (creatorId) {
      getQuestionnaireTemplate({
        creatorId,
        questionnaireTemplateSlug: slug,
        statusOrVersion: 'draft',
        usesCasebuilderQuestionnaire,
      });
    }
  }, [getQuestionnaireTemplate, slug, creatorId, usesCasebuilderQuestionnaire]);

  const getFieldTemplatesForSectionTemplate = useCallback(async (section: Pick<SectionTemplate, 'id' | 'slug' | 'questionnaireTemplateId'>): Promise<void> => {
    if (questionnaireTemplate?.creatorId && section.id) {
      const { data: fields } = await getFieldTemplates({
        creatorId: questionnaireTemplate.creatorId,
        questionnaireTemplateId: section.questionnaireTemplateId,
        sectionTemplateId: section.id,
      });
      if (fields) {
        setSectionFields(prev => ({
          ...prev,
          [section.id]: fields.map((field) => ({
            ...field,
            assignedTo: field.fieldAssigneeTemplates?.map((assignee) => assignee.questionnaireAssigneeTemplateId) ?? [],
            description: field.descriptionText ?? '',
            disabled: false,
            id: field.id,
            validation: field.validation || undefined,
          })),
        }));
      }
    }
  }, [questionnaireTemplate?.creatorId, getFieldTemplates]);

  // @ts-expect-error @todo: fix validation typing issue later
  const formData: Questionnaire.Questionnaire | undefined = useMemo(() =>
    questionnaireTemplate
      ? ({
        ...questionnaireTemplate,
        assigneeTemplates: questionnaireTemplate.questionnaireAssigneeTemplates,
        creatorId,
        description: questionnaireTemplate.descriptionText ?? '',
        id: questionnaireTemplate.id!,
        questionnaireTemplateCreatorId: questionnaireTemplate.creatorId,
        sections: (questionnaireTemplate.sectionTemplates ?? [])
          .map((section) => ({
            ...section,
            description: section.descriptionText ?? '',
            // @todo: support html text later
            fields: sectionFields[section.id] ?? [],
            id: section.id,
            isLoading: !sectionFields[section.id],
          }))
          .sort((a, b) => {
            const rankA = a.rank ?? Number.MAX_SAFE_INTEGER;
            const rankB = b.rank ?? Number.MAX_SAFE_INTEGER;
            return rankA - rankB;
          }),
      })
      : undefined,
  [creatorId, sectionFields, questionnaireTemplate],
  );

  const fieldsByKey: Record<string, Questionnaire.Field> = useMemo(() => formData
    ? formData.sections.reduce((acc, val) => ({
      ...acc,
      ...val.fields.reduce((acc2, val2) => ({
        ...acc2,
        [val2.key]: val2,
      }), []),
    }), [])
    : {}, [formData]);

  if (!creatorId || !questionnaireTemplate) {
    return null;
  }

  return formData
    ? (
      <DynamicForm
        getFieldTemplatesForSectionTemplate={getFieldTemplatesForSectionTemplate}
        fieldsByKey={fieldsByKey}
        questionnaireResponses={[]}
        isCompleted={false}
        isEnabled={false}
        formData={formData}
        onSubmit={async () => true}
        userWorkflowId={null}
      />
    )
    : null;
};

export default QuestionnairePreview;
